import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { useBusinessHours } from "@/hooks/useBusinessHours";
import { useTranslations } from "@/lib/static-intl";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { isApiPlace } from "@/utils/placeUtils";

const LocaldataClosureWarning = ({ risk, variant = "card" }) => {
  if (!risk?.publicWarning) return null;

  const isConfirmed = risk.level === "CONFIRMED_CLOSED";
  const eyebrow = isConfirmed ? "방문 주의" : "영업 정보 확인";

  // "compact" = mobile single-scroll: the detail sentence sits under the phone
  // number instead of shouting from the top of the page. A CONFIRMED closure is
  // already shown as the loud card in the head, so it renders nothing here
  // rather than saying the same thing twice.
  if (variant === "compact") {
    if (isConfirmed) return null;
    return (
      <div
        role="note"
        data-testid="place-closure-warning-compact"
        className="mt-1 flex gap-[7px] border-t border-black/[0.06] dark:border-white/10 pt-[10px]"
      >
        <Iconify
          icon={"mdi:alert-circle-outline"}
          className={
            "!w-[15px] !h-[15px] !min-w-[15px] mt-[1px] !text-orange-500 dark:!text-orange-300"
          }
        />
        <div className="min-w-0">
          <div className="text-t-label font-extrabold tracking-[-0.01em] text-orange-700 dark:text-orange-200">
            {risk.label || "영업 정보 확인 필요"}
          </div>
          <div className="mt-[2px] text-t-label font-medium leading-[1.45] text-ink-45 dark:text-ink_on_dark-45">
            {risk.message ||
              "영업 정보 확인이 필요한 장소입니다. 방문 전 최신 정보를 확인하세요."}
            {risk.latestClosedDate
              ? ` 공공데이터 폐업일 ${risk.latestClosedDate}.`
              : ""}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="place-closure-warning-secondary"
      className={`rounded-chip border px-3 py-2 text-t-sub leading-snug ${
        isConfirmed
          ? "border-red/40 bg-red/10 text-red shadow-sm ring-1 ring-red/15"
          : "border-orange-300 bg-orange-50 text-orange-900 shadow-sm ring-1 ring-orange-100 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-100"
      }`}
    >
      <div className="mb-1 inline-flex rounded-full bg-white/70 px-2 py-0.5 text-t-label font-bold">
        {eyebrow}
      </div>
      <div className="text-t-body font-bold">{risk.label || "영업 정보 확인 필요"}</div>
      <div className="mt-1 font-medium">
        {risk.message ||
          "영업 정보 확인이 필요한 장소입니다. 방문 전 최신 정보를 확인하세요."}
      </div>
      {risk.latestClosedDate && (
        <div className="mt-1 text-t-label opacity-80">
          공공데이터 폐업일: {risk.latestClosedDate}
        </div>
      )}
    </div>
  );
};

const PDW_HomeAddressContactInfoSection = ({
  place,
  showAmenities = true,
  // Mobile single-scroll opt-out: the 폐업 가능성 alert is already shown once in
  // the head (PDW_PlaceDescription). Passing showClosureWarning={false} from the
  // mobile 위치·정보 section removes the duplicate. Defaults true so desktop and
  // every other consumer keep the warning.
  showClosureWarning = true,
  // "card" (default, unchanged for desktop + every other consumer) renders the
  // alert box at the TOP of this section. "compact" renders a quiet one-liner
  // under the phone number instead — mobile single-scroll only.
  closureWarningVariant = "card",
}) => {
  const t = useTranslations("places.place_details_window");
  const { user, openLoginModal } = useAuthContext();
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [isBusinessHourExpanded, setIsBusinessHourExpanded] = useState(false);
  const [isAddressInfoExpanded, setIsAddressInfoExpanded] = useState(false);

  const isOpen = useBusinessHours(place?.businessHours);

  const parseStringifiedArray = (str) => {
    if (!str || str.trim() === "" || str.trim() === "''" || str.trim() === '""') return [];
    try {
      return JSON.parse(str.replace(/'/g, '"'));
    } catch (error) {
      console.error("Invalid input:", error);
      return [];
    }
  };

  const getKSTTimeComponents = () => {
    const now = new Date();

    const kstTime = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(now);

    const kstHour = parseInt(kstTime.find(part => part.type === 'hour').value);
    const kstMinute = parseInt(kstTime.find(part => part.type === 'minute').value);
    const kstDay = parseInt(kstTime.find(part => part.type === 'day').value);

    const kstTimeString = `${kstHour.toString().padStart(2, '0')}:${kstMinute.toString().padStart(2, '0')}`;

    return {
      hour: kstHour,
      minute: kstMinute,
      day: kstDay,
      timeString: kstTimeString
    };
  };

  const isPlaceOpen = (kakaoBusinessHours) => {
    if (!kakaoBusinessHours) return false;

    const kstTime = getKSTTimeComponents();
    const currentTime = kstTime.hour * 60 + kstTime.minute;

    const match = kakaoBusinessHours.match(/(\d{1,2}):(\d{2})\s*~\s*(\d{1,2}):(\d{2})/);
    if (!match) return false;

    const [_, openH, openM, closeH, closeM] = match.map(Number);
    const openTime = openH * 60 + openM;
    const closeTime = closeH * 60 + closeM;

    if (closeTime <= openTime) {
      const isOpen = currentTime >= openTime || currentTime <= closeTime;
      return isOpen;
    } else {
      const isOpen = currentTime >= openTime && currentTime <= closeTime;
      return isOpen;
    }
  };

  const generateDailyHours = (kakaoBusinessHours) => {
    const daysOfWeek = [
      "일요일", "월요일", "화요일", "수요일", "목요일", "금요일", "토요일"
    ];

    const matched = kakaoBusinessHours.match(/(\d{1,2}:\d{2})\s*~\s*(\d{1,2}:\d{2})/);

    let hours = "Closed";
    if (matched) {
      hours = `${matched[1]} - ${matched[2]}`;
    }

    const now = new Date();
    const kstDateString = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
    const kstDate = new Date(kstDateString + 'T00:00:00');
    const todayIndex = kstDate.getDay();

    const rotatedDays = [...daysOfWeek.slice(todayIndex), ...daysOfWeek.slice(0, todayIndex)];

    return rotatedDays.map((day, index) => ({
      day,
      hours,
      isToday: index === 0
    }));
  };

  const getClosingTimeFormatted24 = (kakaoBusinessHours) => {
    const match = kakaoBusinessHours.match(/(\d{1,2}):(\d{2})\s*~\s*(\d{1,2}):(\d{2})/);
    if (!match) return "";

    const [_, openH, openM, closeH, closeM] = match.map(Number);
    return `${closeH.toString().padStart(2, '0')}:${closeM.toString().padStart(2, '0')}`;
  };

  const getOpeningTimeFormatted24 = (kakaoBusinessHours) => {
    const match = kakaoBusinessHours.match(/(\d{1,2}):(\d{2})\s*~\s*(\d{1,2}):(\d{2})/);
    if (!match) return "";

    const [_, openH, openM] = match.map(Number);
    return `${openH.toString().padStart(2, '0')}:${openM.toString().padStart(2, '0')}`;
  };
  const isCurrentlyOpen = isPlaceOpen(place?.kakaoBusinessHours);

  return (
    <div className="w-full flex flex-col gap-3 relative mt-3">
      {showClosureWarning && closureWarningVariant === "card" && (
        <LocaldataClosureWarning risk={place?.localdataClosureRisk} />
      )}

      {place?.dataUpdateDate && place?.dataUpdateDate?.split(" ")[0] && (
        <span className="absolute -top-5 right-0 z-10 text-t-label tracking-tight text-ink-45 dark:text-ink_on_dark-45">
          {t("last updated at")} - {place?.dataUpdateDate?.split(" ")[0]}
        </span>
      )}

      {/* Address */}
      {place?.address && (
        <div className="w-full flex gap-2 items-start tracking-tight">
          <Iconify
            icon={"mdi:location"}
            className={"!text-black/20 dark:!text-white/60 !min-w-4 !min-h-4"}
          />
          <span className="max-w-[90%] text-t-body leading-tight tracking-tight text-ink dark:text-ink_on_dark">
            {place?.address}
          </span>
          <Iconify
            icon={"grommet-icons:down"}
            className={`!text-black/40 dark:!text-white/60 !w-4 !h-4 !min-w-4 transition ${isAddressExpanded && "rotate-180"}`}
            onClick={() => setIsAddressExpanded((prev) => !prev)}
          />
        </div>
      )}
      {isAddressExpanded && (
        <div className="absolute left-0 top-6 z-10 w-full rounded-card bg-surface p-4 shadow-md shadow-black/50 dark:bg-surface-2dark">
          <div className="flex flex-col gap-2">
            <div className="w-full flex items-start justify-between">
              <div className="flex items-start gap-2">
                <div className="h-fit w-fit text-nowrap border border-hairline p-0.5 text-t-label font-medium text-ink-45 dark:border-hairline-dark dark:text-ink_on_dark-45">
                  {t("road name")}
                </div>
                <div className="max-w-full text-t-sub font-medium leading-tight tracking-tight text-ink-70 dark:text-ink_on_dark-70">
                  {place?.address}
                </div>
                <CopyButton copyContent={place?.address} />
              </div>
              <button type="button" onClick={() => setIsAddressExpanded(false)}>
                <Iconify icon={"material-symbols-light:close"} className="!text-black/40 dark:!text-white/40 !min-h-5 !min-w-5" />
              </button>
            </div>
            {place?.old_address && (
              <div className="flex items-start gap-2">
                <div className="h-fit w-fit text-nowrap border border-hairline p-0.5 text-t-label font-medium text-ink-45 dark:border-hairline-dark dark:text-ink_on_dark-45">
                  {t("address")}
                </div>
                <div className="max-w-full text-t-sub leading-tight tracking-tight text-ink-70 dark:text-ink_on_dark-70">
                  {place?.old_address}
                </div>
                <CopyButton copyContent={place?.old_address} />
              </div>
            )}
            {place?.postalCode && (
              <div className="flex items-center gap-2">
                <div className="h-fit w-fit text-nowrap border border-hairline p-0.5 text-t-label font-medium text-ink-45 dark:border-hairline-dark dark:text-ink_on_dark-45">
                  {t("poastal code")}
                </div>
                <div className="text-t-sub font-medium text-ink-70 dark:text-ink_on_dark-70">
                  {place?.postalCode}
                </div>
                <CopyButton copyContent={place?.postalCode} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Address Info */}
      {place?.addressInformation && (
        <div className="w-full flex flex-col gap-1">
          <div className="w-full flex gap-2 items-start tracking-tight">
            <Iconify
              icon={"material-symbols:trending-up"}
              className={"!text-black/20 dark:!text-white/50 !h-5 !w-5 !min-w-5 -rotate-45"}
            />
            <pre className="text-wrap break-words font-sans text-t-sub text-ink-70 first-line:flex dark:text-ink_on_dark-70">
              {isAddressInfoExpanded
                ? place?.addressInformation
                : place?.addressInformation?.split("\n")[0]}
            </pre>
            <Iconify
              icon={"grommet-icons:down"}
              className={`!text-black/40 dark:!text-white/50 !h-4 !w-4 !min-w-4 transition ${isAddressInfoExpanded && "rotate-180"}`}
              onClick={() => setIsAddressInfoExpanded((prev) => !prev)}
            />
          </div>
        </div>
      )}

      {/* Business Hours - Fixed */}
      {place?.kakaoBusinessHours && (
        <div className="w-full flex flex-col gap-2">
          <div className="w-full flex gap-2 items-start tracking-tight">
            <Iconify
              icon="mingcute:time-fill"
              className="!text-black/20 dark:!text-white/50 !h-4 !w-4 !min-w-4 mt-0.5"
            />
            <div className="flex flex-col w-full">
              {/* Status: Open now / Closed */}
              <span className="mb-1 text-t-sub font-normal text-ink dark:text-ink_on_dark">
                {isCurrentlyOpen ? (
                  <>
                    <span className="text-green-600 font-medium">{t("open")}</span> &middot; {t("closes_at")} {getClosingTimeFormatted24(place.kakaoBusinessHours)}
                  </>
                ) : (
                  <>
                    <span className="text-red font-medium">{t("closed")}</span> &middot;{" "}
                    {t("open_at")} {getOpeningTimeFormatted24(place.kakaoBusinessHours)}
                  </>
                )}
              </span>

              {isBusinessHourExpanded ? (
                <div className="flex flex-col gap-1">
                  {generateDailyHours(place.kakaoBusinessHours).map(({ day, hours, isToday }) => {
                    return (
                      <div
                        key={day}
                        className={`flex justify-between text-t-sub ${isToday
                          ? "font-semibold text-black dark:text-white"
                          : "text-black/80 dark:text-white/70"
                          }`}
                      >
                        <span className="w-fit">{day}</span>
                        <span className="w-fit text-right">{hours} (KST 기준)</span>
                      </div>
                    );
                  })}

                  {!isApiPlace(place) && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!user?.userId) {
                          openLoginModal();
                          return;
                        }
                        const placeIdentifier = place?.id || place?._id;
                        if (placeIdentifier) {
                          window.open(`/places/suggest-edit/${placeIdentifier}?tab=business_hours`, "_blank");
                        }
                      }}
                      className="mt-2 text-left text-t-sub text-ink-45 dark:text-ink_on_dark-45"
                    >
                      {t("edit_business_hours") || "Suggest new hours"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-t-sub font-medium text-ink-70 dark:text-ink_on_dark-70">
                  {place.kakaoBusinessHours}
                </div>
              )}
            </div>
            <Iconify
              icon="grommet-icons:down"
              className={`!text-black/40 dark:!text-white/50 !h-4 !w-4 !min-w-4 transition ${isBusinessHourExpanded && "rotate-180"
                }`}
              onClick={() => setIsBusinessHourExpanded((prev) => !prev)}
            />
          </div>
        </div>
      )}

      {/* Phone Number */}
      {place?.phoneNumber && (
        <div className="w-full flex gap-2 items-center tracking-tight">
          <Iconify icon={"ic:round-phone"} className="!text-black/20 dark:!text-white/60 !h-4 !w-4" />
          <span className="truncate text-t-body text-ink dark:text-ink_on_dark">{place?.phoneNumber}</span>
          <Iconify icon={"material-symbols-light:info-outline"} className="!text-black/40 dark:!text-white/60 !h-4 !w-4" />
          <CopyButton copyContent={place?.phoneNumber} />
        </div>
      )}

      {showClosureWarning && closureWarningVariant === "compact" && (
        <LocaldataClosureWarning
          risk={place?.localdataClosureRisk}
          variant="compact"
        />
      )}

      {/* Website */}
      {place?.website && (
        <div className="w-full flex gap-2 items-start tracking-tight">
          <Iconify icon={"jam:world"} className="!text-black/20 dark:!text-white/60 !h-4 !w-4 !min-w-4 mt-1" />
          <PlaceWebAddress websites={parseStringifiedArray(place?.website)} />
        </div>
      )}

      {/* Amenities */}
      {showAmenities && place?.amenities && (
        <div className="w-full flex gap-2 items-start tracking-tight">
          <Iconify
            icon={"heroicons:building-storefront-20-solid"}
            className="!text-black/20 dark:!text-white/60 !h-4 !w-4 !min-w-4 mt-1"
          />
          <span className="text-wrap text-t-sub text-ink-70 dark:text-ink_on_dark-70">{place?.amenities}</span>
        </div>
      )}
    </div>
  );
};

export default PDW_HomeAddressContactInfoSection;

// Reuse these components
const PlaceWebAddress = ({ websites }) => {
  const t = useTranslations("places.place_details_window");

  const [snsLinks] = useState(
    websites?.filter(
      (_web) =>
        _web?.includes("instagram.com") ||
        _web?.includes("facebook.com") ||
        _web?.includes("youtube.com")
    )
  );
  const [nonSnsLinks] = useState(
    websites?.filter(
      (_web) =>
        !(
          _web?.includes("instagram.com") ||
          _web?.includes("facebook.com") ||
          _web?.includes("youtube.com")
        )
    ) || []
  );

  const getHostnameLabel = (link) => {
    try {
      const host = new URL(link).hostname.replace(/^www\./, "");
      return host || "링크";
    } catch {
      return "링크";
    }
  };

  const renderLinkText = (link) => {
    if (link?.includes("instagram.com")) return t("instagram");
    else if (link?.includes("facebook.com")) return t("facebook");
    else if (link?.includes("youtube.com")) return t("youtube");
    else return getHostnameLabel(link);
  };

  return (
    <div className="w-full flex flex-col gap-0.5">
      {nonSnsLinks?.map((web, i) => (
        <a key={i} className="max-w-[98%] truncate text-t-sub text-ink underline underline-offset-4 dark:text-ink_on_dark" href={web} target="_blank">
          {renderLinkText(web)}
        </a>
      ))}
      <div className="w-full flex items-center pt-1">
        {snsLinks?.map((web, i) => (
          <div key={i} className="w-fit flex items-center">
            <a
              className="text-t-sub text-ink underline underline-offset-4 dark:text-ink_on_dark"
              href={web}
              target="_blank"
            >
              {renderLinkText(web)}
            </a>
            {/* 예전엔 마지막 링크 뒤에도 점이 붙어 "인스타그램 ·" 처럼 남았다. */}
            {i < snsLinks.length - 1 && (
              <Iconify icon={"ph:dot-bold"} className={"opacity-50 -mx-1"} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CopyButton = ({ copyContent }) => {
  const t = useTranslations("places.place_details_window");
  const apiT = useTranslations("api");
  const [isCopying, setIsCopying] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyClick = async (contentToBeCopied) => {
    if (!contentToBeCopied) return;
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(contentToBeCopied);
      setIsCopying(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error?.message || apiT("error message"));
      setIsCopying(false);
    }
  };

  const getButtonLabel = () => {
    if (isCopying) {
      return <Iconify icon={"line-md:loading-twotone-loop"} className={"!text-inherit"} />;
    }
    return copied ? (
      <span className="text-inherit text-nowrap">{t("copied")}</span>
    ) : (
      <span className="text-inherit text-nowrap">{t("copy")}</span>
    );
  };

  return (
    <button type="button" className="flex items-center gap-1 rounded-full px-2 py-1 text-t-label text-ink-45 transition hover:bg-ink/[0.06] dark:text-ink_on_dark-45" onClick={() => handleCopyClick(copyContent)} disabled={isCopying}>
      {getButtonLabel()}
    </button>
  );
};
